import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { HiMenuAlt3, HiX, HiOutlineShoppingBag } from "react-icons/hi"; // Agregamos icono de bolsa
import { FiHome, FiLayers, FiMap, FiUsers, FiMail } from "react-icons/fi";

import { HiChevronDown } from "react-icons/hi";


import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "../context/CartContext"; // Importante

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const [isMoreOpen, setIsMoreOpen] = useState(false); // Dropdown Desktop
  const [isMoreMobileOpen, setIsMoreMobileOpen] = useState(false);

  const location = useLocation();
  const { cart, setIsCartOpen } = useCart(); // Traemos el estado del carrito

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Dentro del componente Navbar, añade este useEffect:
  useEffect(() => {
    if (isOpen) {
      // Bloqueamos el scroll y evitamos el "salto" del scrollbar
      document.body.style.overflow = "hidden";
    } else {
      // Restauramos el scroll
      document.body.style.overflow = "unset";
    }

    // Limpieza al desmontar el componente
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const navLinks = [
    { name: "Inicio", path: "/", icon: <FiHome /> },
    { name: "Planos", path: "/planos", icon: <FiMap /> },
    { name: "Servicios", path: "/servicios", icon: <FiLayers /> },
    { name: "Contacto", path: "/contacto", icon: <FiMail /> },
  ];

  const navLinksSecun = [
    { name: "Nosotros", path: "/nosotros", icon: <FiUsers /> },
    { name: "Preguntas", path: "/preguntas-frecuentes", icon: <span>❓</span> },
    { name: "Términos", path: "/terminos-y-condiciones", icon: <span>📜</span> },
    { name: "Pagos", path: "/metodos-de-pago", icon: <span>💳</span> },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed w-full top-0 left-0 z-[100] transition-all duration-500 ${scrolled || isOpen
        ? "py-3 bg-white shadow-md border-b border-gray-100"
        : "py-6 bg-transparent"
        }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">

        {/* Logo */}
        <Link to="/" className="flex items-center gap-1 group">
          <span className="text-2xl md:text-3xl font-black tracking-tighter text-[#1C1C1C]">
            PLA<span className="text-[#F26B1D]">NIX</span>
          </span>
          <div className="w-2 h-2 bg-[#F26B1D] rounded-full mt-2 animate-pulse" />
        </Link>

        {/* Desktop Links */}
        <ul className="hidden lg:flex items-center space-x-2">
          {navLinks.map((link) => (
            <li key={link.path}>
              <Link
                to={link.path}
                className={`flex items-center gap-2 font-bold uppercase tracking-[0.1em] text-[14px] transition-all px-5 py-2.5 rounded-full ${link.name === "Planos"
                  ? "bg-[#1C1C1C] text-white shadow-lg hover:bg-[#F26B1D]"
                  : location.pathname === link.path ? "text-[#F26B1D]" : "text-gray-500 hover:text-[#1C1C1C]"
                  }`}
              >
                {link.name}
              </Link>
            </li>
          ))}

          {/* DROP MENU "MÁS" (Desktop) */}
          <li
            className="relative"
            onMouseEnter={() => setIsMoreOpen(true)}
            onMouseLeave={() => setIsMoreOpen(false)}
          >
            <button className="flex items-center gap-1 font-bold uppercase tracking-[0.1em] text-[13px] px-5 py-2.5 text-gray-500 hover:text-[#1C1C1C] transition-all">
              Más <HiChevronDown className={`transition-transform duration-300 ${isMoreOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {isMoreOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
                  className="absolute top-full left-0 w-52 bg-white shadow-2xl rounded-2xl border border-gray-50 py-3 mt-1 overflow-hidden"
                >
                  {navLinksSecun.map((sublink) => (
                    <Link key={sublink.path} to={sublink.path} className="flex items-center gap-3 px-6 py-3 text-[11px] font-bold text-gray-500 hover:text-[#F26B1D] hover:bg-gray-50 transition-all uppercase tracking-widest">
                      <span className="text-lg opacity-70">{sublink.icon}</span>
                      {sublink.name}
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </li>
        </ul>

        {/* Acciones: Carrito + Auth */}
        <div className="flex items-center gap-3 md:gap-6">

          {/* BOTÓN CARRITO DESKTOP */}
          <button
            onClick={() => setIsCartOpen(true)}
            className="relative p-2.5 bg-gray-100/50 hover:bg-[#F26B1D] hover:text-white rounded-xl transition-all group"
          >
            <HiOutlineShoppingBag className="text-2xl md:text-3xl" />
            {cart.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#F26B1D] group-hover:bg-[#1C1C1C] text-white text-[14px] font-black w-5 h-5 flex items-center justify-center rounded-full border-2 border-white transition-colors">
                {cart.length}
              </span>
            )}
          </button>

          {/* Auth: Solo Desktop */}
          <div className="hidden md:flex items-center gap-4">
            <Link to="/login" className="text-[16px] font-black uppercase tracking-widest text-gray-600 hover:text-[#1C1C1C]">
              Login
            </Link>
            <Link to="/registro" className="px-5 py-2.5 bg-[#1C1C1C] text-white rounded-xl text-[14px] font-black uppercase tracking-widest hover:bg-[#F26B1D] transition-all shadow-md active:scale-95">
              Únete
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button
            className="lg:hidden p-2 text-[#1C1C1C] bg-gray-100 rounded-xl"
            onClick={() => setIsOpen(true)}
          >
            <HiMenuAlt3 size={24} />
          </button>
        </div>
      </div>

      {/* Menú Móvil */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-[#1C1C1C]/40 backdrop-blur-sm z-[110]"
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed inset-y-0 right-0 w-[300px] bg-white z-[120] p-8 shadow-2xl flex flex-col"
            >
              <div className="flex justify-between items-center mb-10">
                <span className="font-black text-xl tracking-tighter">MENÚ</span>
                <button onClick={() => setIsOpen(false)} className="p-2 bg-gray-50 rounded-full">
                  <HiX size={20} />
                </button>
              </div>

              <nav className="flex flex-col gap-6 flex-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center justify-between w-full p-4 rounded-2xl text-sm font-black uppercase tracking-widest transition-all ${link.name === "Planos"
                      ? "bg-[#1C1C1C] text-white shadow-lg shadow-gray-200"
                      : location.pathname === link.path
                        ? "text-[#F26B1D]"
                        : "text-gray-400"
                      }`}
                  >
                    <span className="text-lg">{link.icon}</span>
                    {link.name}
                  </Link>
                ))}
              </nav>

              <div className="mt-4 pt-4 border-t border-gray-100">
                  <button 
                    onClick={() => setIsMoreMobileOpen(!isMoreMobileOpen)}
                    className="flex items-center justify-between w-full p-5 text-[13px] font-black uppercase tracking-widest text-gray-500"
                  >
                    <span>Más Información</span>
                    <HiChevronDown className={`transition-transform duration-300 ${isMoreMobileOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  <AnimatePresence>
                    {isMoreMobileOpen && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden bg-gray-50 rounded-2xl">
                        {navLinksSecun.map((sublink) => (
                          <Link key={sublink.path} to={sublink.path} onClick={() => setIsOpen(false)} className="flex items-center gap-5 p-5 text-[11px] font-bold uppercase tracking-widest text-gray-500">
                            <span className="text-xl opacity-60">{sublink.icon}</span> {sublink.name}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

              <div className="pt-8 border-t space-y-3">
                <Link to="/login" onClick={() => setIsOpen(false)} className="block w-full py-4 text-center font-black text-xs uppercase tracking-widest bg-gray-50 rounded-2xl">
                  Ingresar
                </Link>
                <Link to="/registro" onClick={() => setIsOpen(false)} className="block w-full py-4 text-center font-black text-xs uppercase tracking-widest bg-[#F26B1D] text-white rounded-2xl shadow-lg shadow-orange-200">
                  Crear Cuenta
                </Link>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;